import i18n from '../i18n';
import { env } from './env';
import { getToken } from './auth';

/**
 * Fetch wrapper. Resolves URLs as:
 *   - dev: relative paths (e.g. '/public/health'), Vite proxies to :8787
 *   - prod: prefixed with VITE_API_BASE_URL (e.g. https://v2.api.fly.teraseya.com)
 *
 * Adds x-api-key for /private/* calls, Authorization: Bearer <token> when a
 * token is in storage, and Accept-Language matching the active i18n lang on
 * every call. Throws ApiError on non-2xx.
 */
export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

function resolveUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (env.apiBaseUrl) return `${env.apiBaseUrl.replace(/\/$/, '')}${path}`;
  return path;
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (path.startsWith('/private/') && env.apiKey) {
    headers.set('x-api-key', env.apiKey);
  }
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }
  const token = getToken();
  if (token && !headers.has('authorization')) {
    headers.set('authorization', `Bearer ${token}`);
  }
  if (!headers.has('accept-language')) {
    const lang = i18n.language?.split('-')[0] ?? 'en';
    headers.set('accept-language', lang);
  }

  const res = await fetch(resolveUrl(path), { ...init, headers });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = body as { error?: { code?: string; message?: string } } | null;
    throw new ApiError(
      res.status,
      err?.error?.message ?? res.statusText,
      err?.error?.code,
    );
  }

  return body as T;
}
